import {
  isContentPending,
  isContentStillGenerating,
} from "@/features/campaigns/utils/individualSequenceContentStatus";

export const clampProgress = (value, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Number(value) || 0));

/** Content-only progress — research/profile excluded. */
export function getContentOnlyProgressPercent({ total, contentDone }) {
  const safeTotal = Number(total || 0);
  if (safeTotal <= 0) return 0;
  return clampProgress(
    Math.round((Math.min(Number(contentDone || 0), safeTotal) / safeTotal) * 100),
  );
}

/** Toast formula — respects batch mode (content / profile / account). */
export function getEnrollmentProgressPercent({
  total,
  contentDone,
  profileDone,
  accountDone,
  mode = "campaign",
}) {
  const safeTotal = Number(total || 0);
  if (safeTotal <= 0) return 0;

  const safeContentDone = Math.min(Number(contentDone || 0), safeTotal);
  const safeProfileDone = Math.min(Number(profileDone || 0), safeTotal);
  const safeAccountDone = Math.min(Number(accountDone || 0), safeTotal);

  if (mode === "account") {
    return clampProgress(Math.round((safeAccountDone / safeTotal) * 100));
  }

  if (mode === "campaign") {
    return clampProgress(
      Math.round(((safeContentDone + safeProfileDone) / (safeTotal * 2)) * 100),
    );
  }

  return clampProgress(
    Math.round(
      ((safeContentDone + safeProfileDone + safeAccountDone) / (safeTotal * 3)) *
        100,
    ),
  );
}

export function isEnrollmentBatchComplete({
  total,
  contentDone,
  profileDone,
  accountDone,
  mode = "campaign",
}) {
  const safeTotal = Number(total || 0);
  if (safeTotal <= 0) return false;

  const safeContentDone = Math.min(Number(contentDone || 0), safeTotal);
  const safeProfileDone = Math.min(Number(profileDone || 0), safeTotal);
  const safeAccountDone = Math.min(Number(accountDone || 0), safeTotal);

  if (mode === "account") return safeAccountDone >= safeTotal;
  if (mode === "campaign") {
    return safeContentDone >= safeTotal && safeProfileDone >= safeTotal;
  }
  return (
    safeContentDone >= safeTotal &&
    safeProfileDone >= safeTotal &&
    safeAccountDone >= safeTotal
  );
}

/** Batch content progress — ignores toast dismiss; closing the toast must not affect sequence UI. */
export function isBatchContentInProgress(batchState, campaignId) {
  if (!batchState || !campaignId) return false;
  if (String(batchState.campaignId) !== String(campaignId)) return false;

  const total = Number(batchState.total || 0);
  const contentDone = Number(batchState.contentDone || 0);

  if (total <= 0) return false;
  if (batchState.isComplete) return false;

  return contentDone < total;
}

function isStepContentReady(step) {
  const status = step?.aiTemplate?.status;
  if (status === "GENERATING" || status === "PENDING") return false;

  const body = step?.aiTemplate?.content ?? step?.content ?? "";
  return Boolean(String(body).trim());
}

/** Per-lead content progress from sequence-content API response. */
export function getLeadContentProgressFromSequence(sequenceData) {
  const steps = sequenceData?.result;
  if (!Array.isArray(steps) || steps.length === 0) {
    return { total: 0, contentDone: 0, percent: 0 };
  }

  const total = steps.length;
  const contentDone = steps.filter(isStepContentReady).length;
  const percent = getContentOnlyProgressPercent({ total, contentDone });

  return { total, contentDone, percent };
}

const hasAnyReadyStepContent = (sequenceData) =>
  getLeadContentProgressFromSequence(sequenceData).contentDone > 0;

export function isLeadSequenceContentInProgress(sequenceData) {
  if (!sequenceData) return false;
  // Content that is already written is never hidden behind the generating
  // surface. Steps still being written show their own state inside the
  // sequence, and failed steps show their failure instead of an endless loader.
  // profilePending only concerns the signals panel, which has its own loading
  // state, so it plays no part here either.
  if (hasAnyReadyStepContent(sequenceData)) return false;
  if (isContentStillGenerating(sequenceData)) return true;
  if (sequenceData.isContentGenerated === true) return false;

  return isContentPending(sequenceData);
}

export function shouldShowSequenceGeneratingLoader({
  campaignId,
  batchState,
  sequenceData,
  isLoading,
  isFetching = false,
}) {
  if (isLeadSequenceContentInProgress(sequenceData)) return true;

  if (isBatchContentInProgress(batchState, campaignId)) {
    const waitingForLeadData = isLoading || isFetching;
    if (!sequenceData && waitingForLeadData) return true;
    if (
      sequenceData?.isContentGenerated === false &&
      !hasAnyReadyStepContent(sequenceData)
    ) {
      return true;
    }
  }

  return false;
}

export function shouldShowSequenceNormalLoader({
  isUpdating,
  isLoading,
  showGeneratingLoader,
}) {
  if (isUpdating) return true;
  if (showGeneratingLoader) return false;
  if (isLoading) return true;
  return false;
}

/** Content-only snapshot for the animated sequence loader. */
export function getSequenceContentProgressSnapshot({
  batchState,
  campaignId,
  sequenceData,
}) {
  const leadProgress = getLeadContentProgressFromSequence(sequenceData);
  if (leadProgress.total > 0) {
    return leadProgress;
  }

  if (isBatchContentInProgress(batchState, campaignId)) {
    const total = Number(batchState.total || 0);
    const contentDone = Math.min(Number(batchState.contentDone || 0), total);

    return {
      total,
      contentDone,
      percent: getContentOnlyProgressPercent({ total, contentDone }),
    };
  }

  return leadProgress;
}

/** @deprecated use getSequenceContentProgressSnapshot */
export function getSequenceProgressSnapshot(args) {
  return getSequenceContentProgressSnapshot(args);
}
